'use client';

import { Link } from '@/i18n/routing';
import { securityLocale } from '@/lib/security/public-policy';
import { useEffect, useMemo, useState } from 'react';
import { Facebook, Instagram, Linkedin, Mail, Music2, ShieldCheck, Star, Truck, Youtube, Phone } from 'lucide-react';
import { useLocale } from 'next-intl';
import { orderTrackingPath, productPath, productsPath } from '@/lib/localized-paths';
import { MONEY_PAGE_HREF, MONEY_PAGE_IDS, moneyPageNavLabel } from '@/lib/seo/money-pages';
import { companyInfo, phoneDisplayFor } from '@/config/company';
import { CheckshopWidgetFrame } from '@/components/checkshop-widget-frame';
import { fetchSiteSettings } from '@/lib/client/site-settings-cache';
import { SpecialistBrandMark } from '@/components/niche/specialist-brand-mark';
import { defaultPaymentLogo, resolveFooterPaymentMethods } from '@/lib/commerce/checkout-payment-methods';

interface Category {
  slug: string;
  name: string;
  count: number;
}

interface FooterSiteProfile {
  id: string;
  brandName: string;
  parentBrand: string;
  navigation?: Array<{ label: string; href: string; description?: string }>;
  trustCopy?: { tagline: string; promise: string; footerNote: string } | null;
}

interface FooterProps {
  initialSiteProfile?: FooterSiteProfile | null;
}

const defaultShippingByLocale: Record<string, string[]> = {
  nl: ['PostNL', 'DHL', 'DPD'],
  de: ['DHL', 'DPD'],
  fr: ['DPD', 'DHL'],
};

const legacyGlobalShipping = 'postnl, dhl, dpd';
const footerCategorySlugs = [
  'canvas',
  'wanddecoratie',
  'posters',
  'fotoprints',
  'stickers-tapes',
  'paper-print',
  'flyers',
  'visitekaartjes',
  'outdoor-advertising',
  'spandoeken-banners',
  'vlaggen',
  'relatiegeschenken',
  'keuken-interieur',
];

const footerCategoryLabels: Record<string, Record<string, string>> = {
  canvas: { de: 'Foto auf Leinwand', en: 'Photo on canvas', fr: 'Photo sur toile' },
  wanddecoratie: { de: 'Wanddekoration', en: 'Wall decoration', fr: 'Décoration murale' },
  posters: { de: 'Poster', en: 'Posters', fr: 'Posters' },
  fotoprints: { de: 'Fotoabzüge', en: 'Photo prints', fr: 'Tirages photo' },
  'stickers-tapes': { de: 'Aufkleber', en: 'Stickers', fr: 'Stickers' },
  'paper-print': { de: 'Drucksachen', en: 'Print products', fr: 'Imprimés' },
  flyers: { de: 'Flyer', en: 'Flyers', fr: 'Flyers' },
  visitekaartjes: { de: 'Visitenkarten', en: 'Business cards', fr: 'Cartes de visite' },
  'outdoor-advertising': { de: 'Außenwerbung', en: 'Outdoor advertising', fr: 'Publicité extérieure' },
  'spandoeken-banners': { de: 'Banner & Planen', en: 'Banners', fr: 'Bâches et bannières' },
  vlaggen: { de: 'Flaggen', en: 'Flags', fr: 'Drapeaux' },
  relatiegeschenken: { de: 'Werbeartikel', en: 'Promotional gifts', fr: 'Objets publicitaires' },
  'keuken-interieur': { de: 'Küchendrucke', en: 'Kitchen prints', fr: 'Impressions cuisine' },
};

function footerCategoryLabel(category: Category, locale: string) {
  return footerCategoryLabels[category.slug]?.[locale] || category.name;
}

function logoSlug(name: string) {
  return name
    .toLowerCase()
    .replace(/\+/g, 'plus')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

function defaultShippingLogo(name: string) {
  const normalized = name.toLowerCase();
  return (
    normalized.includes('postnl') ? '/logos/shipping/postnl.svg' :
    normalized.includes('dhl') ? '/logos/shipping/dhl.svg' :
    normalized.includes('dpd') ? '/logos/shipping/dpd.svg' :
    null
  );
}

function PaymentLogo({ name, uploadedSrc }: { name: string; uploadedSrc?: string }) {
  const src =
    uploadedSrc ||
    defaultPaymentLogo(name);

  return (
    <span className="inline-flex h-9 min-w-20 items-center justify-center rounded-md bg-white p-1 shadow-sm ring-1 ring-white/20">
      {src ? (
        <img src={src} alt={name} className="h-full max-w-28 object-contain" loading="lazy" />
      ) : (
        <span className="px-2 text-xs font-black text-slate-900">{name}</span>
      )}
    </span>
  );
}

function ShippingLogo({ name, uploadedSrc }: { name: string; uploadedSrc?: string }) {
  const src = uploadedSrc || defaultShippingLogo(name);

  return (
    <span className="inline-flex h-10 w-24 items-center justify-center rounded-md bg-white px-2 py-1 shadow-sm ring-1 ring-white/20">
      {src ? (
        <img src={src} alt={name} className="max-h-7 max-w-20 object-contain" loading="lazy" />
      ) : (
        <span className="px-2 text-xs font-black text-slate-900">{name}</span>
      )}
    </span>
  );
}

function splitList(value: string) {
  return value.split(',').map((item) => item.trim()).filter(Boolean);
}

export function Footer({ initialSiteProfile = null }: FooterProps) {
  const locale = useLocale();
  const [categories, setCategories] = useState<Category[]>([]);
  const [marketing, setMarketing] = useState({
    facebook_url: '',
    instagram_url: '',
    pinterest_url: '',
    linkedin_url: '',
    youtube_url: '',
    tiktok_url: '',
    footer_payment_methods: '',
    footer_shipping_partners: '',
  });
  const [footerLogos, setFooterLogos] = useState<{
    payments?: Record<string, { url: string }>;
    shipping?: Record<string, { url: string }>;
  }>({});
  const [siteLogo, setSiteLogo] = useState<{ url: string } | null>(null);
  const [siteProfile, setSiteProfile] = useState<FooterSiteProfile | null>(initialSiteProfile);

  useEffect(() => {
    fetch(`/api/categories?locale=${locale}`)
      .then((res) => res.json())
      .then((data) => setCategories(data))
      .catch((err) => console.error('Error loading categories:', err));
  }, [locale]);

  useEffect(() => {
    fetchSiteSettings()
      .then((data: any) => {
        if (data.marketing) setMarketing((current) => ({ ...current, ...data.marketing }));
        if (data.site) setSiteProfile(data.site);
        if (data.site?.id === 'printen' && data.logo?.url) setSiteLogo({ url: data.logo.url });
        if (data.site?.id && data.site.id !== 'printen') setSiteLogo(null);
        if (data.footerLogos) setFooterLogos(data.footerLogos);
      })
      .catch((err) => console.error('Error loading site settings:', err));
  }, []);

  const copy = {
    nl: {
      tagline: 'Foto, stickers, buitenreclame en drukwerk voor thuis en bedrijf.',
      products: 'Producten',
      service: 'Klantenservice',
      business: 'Zakelijk',
      businessOrders: 'Zakelijk bestellen',
      contact: 'Contact',
      files: 'Bestandseisen',
      shipping: 'Verzending',
      returns: 'Retourneren',
      rights: 'Alle rechten voorbehouden',
      privacy: 'Privacy',
      terms: 'Algemene voorwaarden',
      imprint: 'Bedrijfsgegevens',
      cookies: 'Cookies',
      cookieSettings: 'Cookievoorkeuren',
      promise: 'Veilig betalen, snelle productie en thuisbezorgd in Nederland, België, Duitsland en 16 andere Europese landen.',
      allProducts: 'Alle producten',
      paymentMethods: 'Betaalmethodes',
      shippingPartners: 'Verzendpartijen',
      reviews: 'Reviews',
      checkshop: 'CheckShop profiel',
      trackOrder: 'Bestelling volgen',
      kb: 'Kennisbank',
      socialsTitle: 'Volg Printen.eu',
      socialsText: 'Bekijk nieuwe producten, printinspiratie en voorbeelden van klanten.',
      socialsCta: 'Volg ons voor inspiratie',
      collaboration: 'Samenwerken',
      affiliate: 'Affiliate programma',
      supplierNetwork: 'Word productieleverancier',
      resellerProgram: 'Wederverkopersprogramma',
      supportHours: 'Support: antwoord binnen 1 werkdag, kantooruren 09:00-17:30 (NL).',
      guarantee: 'Garantie',
    },
    en: {
      tagline: 'Photo products, stickers, signage and print for home and business.',
      products: 'Products', service: 'Customer service', business: 'Business', businessOrders: 'Business printing', contact: 'Contact', files: 'Artwork requirements', shipping: 'Shipping', returns: 'Returns', rights: 'All rights reserved', privacy: 'Privacy', terms: 'Terms and conditions', imprint: 'Company details', cookies: 'Cookies', cookieSettings: 'Cookie preferences', promise: 'Secure payment, reliable production and delivery throughout Europe.', supportHours: 'Support: reply within 1 business day, office hours 09:00–17:30 (NL).', allProducts: 'All products', paymentMethods: 'Payment methods', shippingPartners: 'Shipping partners', reviews: 'Reviews', checkshop: 'CheckShop profile', trackOrder: 'Track order', kb: 'Knowledge base', socialsTitle: 'Follow Printen.eu', socialsText: 'Discover new products, print inspiration and customer examples.', socialsCta: 'Follow us for inspiration', collaboration: 'Installation partnership', affiliate: 'Affiliate programme', supplierNetwork: 'Become a production supplier', resellerProgram: 'Reseller programme', guarantee: 'Guarantee',
    },
    de: {
      tagline: 'Foto, Aufkleber, Banner und Drucksachen für Zuhause und Geschäft.',
      products: 'Produkte',
      service: 'Kundenservice',
      business: 'Geschäftskunden',
      businessOrders: 'Geschäftlich bestellen',
      contact: 'Kontakt',
      files: 'Dateianforderungen',
      shipping: 'Versand',
      returns: 'Rückgabe',
      rights: 'Alle Rechte vorbehalten',
      privacy: 'Datenschutz',
      terms: 'AGB',
      imprint: 'Impressum',
      cookies: 'Cookies',
      cookieSettings: 'Cookie-Einstellungen',
      promise: 'Sicher bezahlen, schnelle Produktion und Lieferung nach Hause.',
      supportHours: 'Support: Antwort innerhalb 1 Werktag, Bürozeiten 09:00-17:30 (NL).',
      allProducts: 'Alle Produkte',
      paymentMethods: 'Zahlungsarten',
      shippingPartners: 'Versandpartner',
      reviews: 'Bewertungen',
      checkshop: 'CheckShop Profil',
      trackOrder: 'Bestellung verfolgen',
      kb: 'Wissensdatenbank',
      socialsTitle: 'Printen.eu folgen',
      socialsText: 'Neue Produkte, Druckideen und Kundenbeispiele entdecken.',
      socialsCta: 'Folge uns für Inspiration',
      collaboration: 'Zusammenarbeiten',
      affiliate: 'Affiliate-Programm',
      supplierNetwork: 'Produktionslieferant werden',
      resellerProgram: 'Wiederverkäuferprogramm',
      guarantee: 'Garantie',
    },
    fr: {
      tagline: 'Photos, stickers, signalétique extérieure et imprimés pour particuliers et entreprises.',
      products: 'Produits',
      service: 'Service client',
      business: 'Professionnels',
      businessOrders: 'Commander pour entreprise',
      contact: 'Contact',
      files: 'Exigences fichiers',
      shipping: 'Livraison',
      returns: 'Retours',
      rights: 'Tous droits réservés',
      privacy: 'Confidentialité',
      terms: 'Conditions générales',
      imprint: 'Mentions légales',
      cookies: 'Cookies',
      cookieSettings: 'Préférences de cookies',
      promise: 'Paiement sécurisé, production rapide et livraison à domicile.',
      supportHours: 'Support : réponse sous 1 jour ouvré, horaires 09:00-17:30 (NL).',
      allProducts: 'Tous les produits',
      paymentMethods: 'Moyens de paiement',
      shippingPartners: 'Transporteurs',
      reviews: 'Avis',
      checkshop: 'Profil CheckShop',
      trackOrder: 'Suivre commande',
      kb: 'Base de connaissances',
      socialsTitle: 'Suivez Printen.eu',
      socialsText: 'Découvrez les nouveaux produits, idées d’impression et exemples clients.',
      socialsCta: 'Suivez-nous pour l’inspiration',
      collaboration: 'Collaborer',
      affiliate: 'Programme d’affiliation',
      supplierNetwork: 'Devenir fournisseur',
      resellerProgram: 'Programme revendeurs',
      guarantee: 'Garantie',
    },
  };
  const t = copy[locale as keyof typeof copy] || copy.nl;
  const isStickerSite = siteProfile?.id === 'stickers';
  const isBoardSite = siteProfile?.id === 'reclameborden';
  const isPosterSite = siteProfile?.id === 'posters';
  const isCanvasSite = siteProfile?.id === 'canvas';
  const isSpecialistSite = Boolean(siteProfile && siteProfile.id !== 'printen');
  const footerBrandName = siteProfile?.brandName || 'Printen.eu';
  const footerTagline = siteProfile?.trustCopy?.tagline || t.tagline;
  const footerPromise = siteProfile?.trustCopy?.promise || t.promise;
  const footerNote = siteProfile?.trustCopy?.footerNote;

  const paymentMethods = useMemo(() => {
    return resolveFooterPaymentMethods(splitList(marketing.footer_payment_methods), locale);
  }, [locale, marketing.footer_payment_methods]);

  const shippingPartners = useMemo(() => {
    const configured = splitList(marketing.footer_shipping_partners);
    const configuredKey = configured.join(', ').toLowerCase();
    return configured.length > 0 && configuredKey !== legacyGlobalShipping
      ? configured
      : (defaultShippingByLocale[locale] || defaultShippingByLocale.nl);
  }, [locale, marketing.footer_shipping_partners]);

  const footerCategories = useMemo(() => {
    if (isStickerSite || isBoardSite || isPosterSite || isCanvasSite) {
      return isCanvasSite ? [
        { slug: 'canvas', name: 'Foto op canvas', count: 1 },
        { slug: 'fotopaneel-met-baklijst', name: 'Fotopaneel met baklijst', count: 1 },
      ] : isBoardSite ? [
        { slug: 'reclamebord', name: 'Reclameborden', count: 1 },
        { slug: 'gevelbord', name: 'Gevelborden', count: 1 },
        { slug: 'vlak-makelaarsbord', name: 'Makelaarsborden', count: 1 },
        { slug: 'a-borden', name: 'Stoepborden', count: 1 },
        { slug: 'sportveldbord', name: 'Sportveldborden', count: 1 },
        { slug: 'dibond', name: 'Dibond / Forex', count: 1 },
      ] : isPosterSite ? [
        { slug: 'foto-op-poster', name: 'Foto op poster', count: 1 },
        { slug: 'ingelijste-poster', name: 'Ingelijste poster', count: 1 },
        { slug: 'textielposter', name: 'Textielposter', count: 1 },
        { slug: 'watervaste-poster', name: 'Watervaste poster', count: 1 },
        { slug: 'abri-poster', name: 'Abri poster', count: 1 },
        { slug: 'tuinposter', name: 'Tuinposter', count: 1 },
      ] : [
        { slug: 'vinylsticker', name: 'Vinylstickers', count: 1 },
        { slug: 'autosticker', name: 'Autostickers', count: 1 },
        { slug: 'kiss-cut-stickers-met-print-673', name: 'Logo stickers in eigen vorm', count: 1 },
        { slug: 'stickervellen', name: 'Verpakkingsstickers', count: 1 },
        { slug: 'raamstickers', name: 'Raamstickers', count: 1 },
        { slug: 'holografische-stickers', name: 'Holografisch', count: 1 },
      ];
    }

    const categoryBySlug = new Map(categories.map((category) => [category.slug, category]));
    const selected = footerCategorySlugs
      .map((slug) => categoryBySlug.get(slug))
      .filter((category): category is Category => Boolean(category));

    return selected.length > 0 ? selected : categories.slice(0, 12);
  }, [categories, isBoardSite, isCanvasSite, isPosterSite, isStickerSite]);

  const socialLinks = [
    {
      name: 'Instagram',
      href: marketing.instagram_url || companyInfo.social.instagram,
      icon: Instagram,
      handle: '@printen.eu',
    },
    {
      name: 'Facebook',
      href: marketing.facebook_url || companyInfo.social.facebook,
      icon: Facebook,
      handle: 'Printen.eu',
    },
    {
      name: 'Pinterest',
      href: marketing.pinterest_url,
      icon: null,
      handle: 'Pinterest',
    },
    {
      name: 'LinkedIn',
      href: marketing.linkedin_url || companyInfo.social.linkedin,
      icon: Linkedin,
      handle: 'LinkedIn',
    },
    {
      name: 'YouTube',
      href: marketing.youtube_url || companyInfo.social.youtube,
      icon: Youtube,
      handle: 'YouTube',
    },
    {
      name: 'TikTok',
      href: marketing.tiktok_url,
      icon: Music2,
      handle: '@printen.eu',
    },
  ].filter((social) => Boolean(social.href));

  return (
    <footer className={`border-t ${isStickerSite ? 'border-lime-200' : isBoardSite ? 'border-amber-300' : isPosterSite ? 'border-red-200' : isCanvasSite ? 'border-stone-200' : 'border-rose-100'} bg-slate-950 text-white`}>
      <div className="container mx-auto px-4 pt-12 pb-[calc(6rem+env(safe-area-inset-bottom))]">
        <div className="grid gap-8 md:grid-cols-2 xl:grid-cols-[1.35fr_repeat(4,minmax(0,1fr))]">
          <div>
            <div className="flex h-12 items-center">
              {isSpecialistSite ? (
                <SpecialistBrandMark siteId={siteProfile?.id || 'stickers'} dark />
              ) : siteLogo?.url ? (
                <img
                  src={siteLogo.url}
                  alt="Printen.eu"
                  className="h-12 max-w-[210px] object-contain brightness-0 invert"
                  loading="lazy"
                />
              ) : (
                <span className="text-2xl font-black text-white">{footerBrandName}</span>
              )}
            </div>
            <p className="mt-3 text-sm leading-6 text-slate-300">{footerTagline}</p>
            <div className="mt-5 flex items-start gap-3 rounded-2xl bg-white/10 p-4">
              <ShieldCheck className={`mt-0.5 h-5 w-5 shrink-0 ${isStickerSite ? 'text-lime-300' : isBoardSite ? 'text-amber-300' : isPosterSite ? 'text-red-300' : isCanvasSite ? 'text-stone-300' : 'text-emerald-400'}`} />
              <p className="text-sm text-slate-200">{footerPromise}</p>
              {'supportHours' in t ? <p className="text-sm text-slate-300 mt-2">{t.supportHours}</p> : null}
            </div>
            {footerNote && (
              <p className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-4 text-xs font-semibold leading-5 text-slate-300">
                {footerNote}
              </p>
            )}
            <div className="mt-4 rounded-2xl border border-rose-400/30 bg-rose-500/10 p-4">
              <p className="text-sm font-black text-white">{isStickerSite ? 'Volg BestelStickers.nl' : isBoardSite ? 'Volg BestelReclamebord.nl' : isPosterSite ? 'Volg BestelPosters.nl' : isCanvasSite ? 'Volg BestelFotoCanvas.nl' : t.socialsTitle}</p>
              <p className="mt-1 text-sm leading-6 text-slate-300">{t.socialsText}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {socialLinks.map((social) => {
                  const Icon = social.icon;
                  return (
                    <a
                      key={social.name}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-2 text-sm font-black text-slate-950 shadow-sm transition hover:bg-rose-100 hover:text-rose-700"
                      aria-label={`${t.socialsCta}: ${social.name}`}
                    >
                      {Icon ? (
                        <Icon className="h-4 w-4" />
                      ) : (
                        /* Pinterest zit niet in lucide; klein inline merk-glyph. */
                        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden>
                          <path d="M12 2C6.48 2 2 6.48 2 12c0 4.1 2.47 7.61 6 9.15-.08-.78-.16-1.98.03-2.83l1.16-4.92s-.3-.6-.3-1.47c0-1.38.8-2.41 1.8-2.41.85 0 1.26.64 1.26 1.4 0 .85-.54 2.13-.82 3.32-.24.99.5 1.79 1.47 1.79 1.76 0 3.11-1.86 3.11-4.54 0-2.37-1.7-4.03-4.14-4.03-2.82 0-4.47 2.11-4.47 4.3 0 .85.33 1.76.74 2.26.08.1.09.18.07.28l-.28 1.13c-.04.18-.15.22-.34.13-1.25-.58-2.03-2.4-2.03-3.87 0-3.15 2.29-6.04 6.6-6.04 3.47 0 6.16 2.47 6.16 5.77 0 3.45-2.17 6.22-5.19 6.22-1.01 0-1.97-.53-2.29-1.15l-.62 2.37c-.23.87-.84 1.96-1.25 2.63.94.29 1.93.45 2.97.45 5.52 0 10-4.48 10-10S17.52 2 12 2z" />
                        </svg>
                      )}
                      {social.handle}
                    </a>
                  );
                })}
              </div>
            </div>
          </div>

          <div>
            <h3 className="mb-4 text-sm font-black uppercase tracking-wide text-slate-400">{t.products}</h3>
            <ul className="grid gap-x-4 gap-y-2 text-sm sm:grid-cols-2 md:grid-cols-1">
              {!isSpecialistSite && MONEY_PAGE_IDS.map((id) => (
                <li key={id}>
                  <Link href={MONEY_PAGE_HREF[id]} className="font-bold text-slate-200 transition hover:text-rose-300">
                    {moneyPageNavLabel(id, locale)}
                  </Link>
                </li>
              ))}
              {footerCategories.map((category) => (
                <li key={category.slug}>
                  <Link href={isSpecialistSite ? productPath(locale, category.slug) : productsPath(locale, { category: category.slug })} className={`text-slate-300 transition ${isStickerSite ? 'hover:text-lime-300' : isBoardSite ? 'hover:text-amber-300' : isPosterSite ? 'hover:text-red-300' : isCanvasSite ? 'hover:text-stone-300' : 'hover:text-rose-300'}`}>
                    {footerCategoryLabel(category, locale)}
                  </Link>
                </li>
              ))}
              <li>
                <Link href={productsPath(locale)} className={`font-bold transition hover:text-white ${isStickerSite ? 'text-lime-300' : isBoardSite ? 'text-amber-300' : isPosterSite ? 'text-red-300' : isCanvasSite ? 'text-stone-300' : 'text-rose-300'}`}>
                  {isStickerSite ? 'Alle stickers' : isBoardSite ? 'Alle reclameborden' : isPosterSite ? 'Alle posters' : isCanvasSite ? 'Alle canvasproducten' : t.allProducts}
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="mb-4 text-sm font-black uppercase tracking-wide text-slate-400">{t.service}</h3>
            <ul className="space-y-2 text-sm">
              <li><Link href="/kennisbank" className="text-slate-300 transition hover:text-rose-300">{t.kb}</Link></li>
              {!isSpecialistSite && <li><Link href="/materialen" className="text-slate-300 transition hover:text-rose-300">{locale === 'de' ? 'Materialien' : locale === 'fr' ? 'Matériaux' : locale === 'en' ? 'Materials' : 'Materialen'}</Link></li>}
              {!isSpecialistSite && <li><Link href="/afwerkingen" className="text-slate-300 transition hover:text-rose-300">{locale === 'de' ? 'Veredelungen' : locale === 'fr' ? 'Finitions' : locale === 'en' ? 'Finishes' : 'Afwerkingen'}</Link></li>}
              <li><Link href="/bestandseisen" className="text-slate-300 transition hover:text-rose-300">{t.files}</Link></li>
              <li><Link href="/tools/dpi-checker" className="text-slate-300 transition hover:text-rose-300">{locale === 'fr' ? 'Vérificateur DPI' : 'DPI-checker'}</Link></li>
              <li><Link href={orderTrackingPath(locale)} className="text-slate-300 transition hover:text-rose-300">{t.trackOrder}</Link></li>
              <li><Link href="/verzending" className="text-slate-300 transition hover:text-rose-300">{t.shipping}</Link></li>
              <li><Link href="/retourneren" className="text-slate-300 transition hover:text-rose-300">{t.returns}</Link></li>
              <li><Link href="/garantie" className="text-slate-300 transition hover:text-rose-300">{t.guarantee}</Link></li>
              <li><Link href="/faq" className="text-slate-300 transition hover:text-rose-300">{locale === 'de' ? 'Häufige Fragen' : locale === 'fr' ? 'Questions fréquentes' : locale === 'en' ? 'FAQ' : 'Veelgestelde vragen'}</Link></li>
              <li><Link href="/contact" className="text-slate-300 transition hover:text-rose-300">{isSpecialistSite ? 'Contact / advies' : t.contact}</Link></li>
            </ul>
          </div>

          {!isSpecialistSite ? (
            <div>
              <h3 className="mb-4 text-sm font-black uppercase tracking-wide text-slate-400">{t.business}</h3>
              <ul className="space-y-2 text-sm">
                <li><Link href="/zakelijk" className="font-bold text-slate-200 transition hover:text-rose-300">{t.businessOrders}</Link></li>
                <li><Link href="/samenwerken" className="text-slate-300 transition hover:text-rose-300">{t.collaboration}</Link></li>
                <li><Link href="/leverancier-worden" className="text-slate-300 transition hover:text-rose-300">{t.supplierNetwork}</Link></li>
                <li><Link href="/wederverkopers" className="text-slate-300 transition hover:text-rose-300">{t.resellerProgram}</Link></li>
                <li><Link href="/affiliate" className="text-slate-300 transition hover:text-rose-300">{t.affiliate}</Link></li>
              </ul>
            </div>
          ) : null}

          <div>
            <h3 className="mb-4 text-sm font-black uppercase tracking-wide text-slate-400">{t.contact}</h3>
            <ul className="space-y-3 text-sm">
              <li className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-rose-300" />
                <a href="mailto:info@printen.eu" className="text-slate-300 transition hover:text-rose-300">info@printen.eu</a>
              </li>
              <li className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-rose-300" />
                <a href={`tel:${companyInfo.contact.phone.replace(/\s/g, '')}`} className="text-slate-300 transition hover:text-rose-300">{phoneDisplayFor(locale)}</a>
              </li>
              <li className="flex items-center gap-2">
                <Instagram className="h-4 w-4 text-rose-300" />
                <a href={marketing.instagram_url || companyInfo.social.instagram} target="_blank" rel="noopener noreferrer" className="text-slate-300 transition hover:text-rose-300">Instagram</a>
              </li>
              <li className="flex items-center gap-2">
                <Facebook className="h-4 w-4 text-rose-300" />
                <a href={marketing.facebook_url || companyInfo.social.facebook} target="_blank" rel="noopener noreferrer" className="text-slate-300 transition hover:text-rose-300">Facebook</a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-10 grid gap-4 border-t border-white/10 pt-6 md:grid-cols-2">
          <div>
            <h3 className="mb-3 text-xs font-black uppercase tracking-wide text-slate-400">{t.paymentMethods}</h3>
            <div className="flex flex-wrap gap-2">
              {paymentMethods.map((method) => (
                <PaymentLogo
                  key={method}
                  name={method}
                  uploadedSrc={footerLogos.payments?.[logoSlug(method)]?.url}
                />
              ))}
            </div>
          </div>
          <div>
            <h3 className="mb-3 flex items-center gap-2 text-xs font-black uppercase tracking-wide text-slate-400">
              <Truck className="h-4 w-4" />
              {t.shippingPartners}
            </h3>
            <div className="flex flex-wrap gap-2">
              {shippingPartners.map((partner) => (
                <ShippingLogo
                  key={partner}
                  name={partner}
                  uploadedSrc={footerLogos.shipping?.[logoSlug(partner)]?.url}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="mt-8 border-t border-white/10 pt-6">
          <h3 className="mb-3 flex items-center gap-2 text-xs font-black uppercase tracking-wide text-slate-400">
            <Star className="h-4 w-4" />
            {t.reviews}
          </h3>
          <div className="checkshop-badge-embed inline-flex min-h-10 items-center">
            <CheckshopWidgetFrame
              type="badge"
              title={t.checkshop}
              locale={locale}
              height={70}
              variant="horizontal"
              position="inline"
              className="max-w-sm"
            />
          </div>
        </div>

        <div className="mt-10 flex flex-col justify-between gap-4 border-t border-white/10 pt-6 text-sm text-slate-400 md:flex-row md:items-center">
          <p>Copyright {new Date().getFullYear()} {footerBrandName}. {t.rights}.</p>
          <div className="flex flex-wrap gap-5">
            <Link href="/privacy" className="hover:text-rose-300">{t.privacy}</Link>
            <a href={`/${securityLocale(locale)}/security`} className="hover:text-rose-300">{{ nl: 'Beveiliging', en: 'Security', de: 'Sicherheit', fr: 'Sécurité' }[securityLocale(locale)]}</a>
            <Link href="/algemene-voorwaarden" className="hover:text-rose-300">{t.terms}</Link>
            <Link href="/impressum" className="hover:text-rose-300">{t.imprint}</Link>
            <Link href="/cookies" className="hover:text-rose-300">{t.cookies}</Link>
            <button
              type="button"
              className="min-h-11 text-left hover:text-rose-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-300 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950"
              onClick={() => window.dispatchEvent(new Event('printen-open-cookie-settings'))}
            >
              {t.cookieSettings}
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}
